import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CalendarPlus, ChevronsDownUp, ChevronsUpDown, ListTodo, Plus } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";

import {
  useDeleteDeliveryTask,
  useDeliveryTasks,
  useUpdateDeliveryTask,
} from "@/application/use-cases/delivery-tasks/use-delivery-tasks.queries";
import { useDeleteSprint, useSprints } from "@/application/use-cases/sprints/use-sprints.queries";
import { useWorkstreams } from "@/application/use-cases/board/use-board.queries";
import { useAllProfiles } from "@/application/use-cases/profile/use-profile.queries";
import { ActionButton } from "@/presentation/components/delivery-tasks/action-button";
import { CreateDeliveryTaskDialog } from "@/presentation/components/delivery-tasks/create-delivery-task-dialog";
import { SprintDialog } from "@/presentation/components/delivery-tasks/sprint-dialog";
import { DeliveryTaskDrawerContent } from "@/presentation/components/delivery-tasks/delivery-task-drawer-content";
import { SprintSection } from "@/presentation/components/delivery-tasks/sprint-section";
import {
  formatSprintRange,
  nextSprintName,
  nextSprintStart,
  sprintTiming,
} from "@/presentation/components/delivery-tasks/sprint-dates";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import type { DeliveryTaskPatch } from "@/infrastructure/repositories/delivery-tasks.supabase.repository";
import type {
  DeliveryTaskRow,
  SprintRow,
  TaskStatus,
} from "@/infrastructure/supabase/database.types";

const BACKLOG_GROUP_ID = "__backlog__";

/**
 * Per-call error options for the inline (dialog-less) mutations. A toast fires
 * once per failed action and then goes away — a rendered `isError` banner would
 * stay on screen indefinitely after one transient failure, including after
 * later edits succeeded.
 */
function failsWith(what: string) {
  return { onError: () => toast.error(`${what} — you may not have permission.`) };
}

const searchSchema = z.object({ id: z.string().optional() });

export const Route = createFileRoute("/_app/delivery-tasks/")({
  validateSearch: searchSchema,
  component: DeliveryTasksPage,
});

function DeliveryTasksPage() {
  const { isAdmin, loading } = useWorkspace();

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6 lg:p-10">
        <p className="text-muted-foreground text-xs italic">Checking access…</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6 lg:p-10">
        <p className="text-muted-foreground text-sm">
          This page is for admins only. Ask an admin to grant you access.
        </p>
      </div>
    );
  }

  return <SprintBoard />;
}

function SprintBoard() {
  const tasksQ = useDeliveryTasks();
  const sprintsQ = useSprints();
  const workstreamsQ = useWorkstreams();
  const profilesQ = useAllProfiles();
  const updateTask = useUpdateDeliveryTask();
  const deleteTask = useDeleteDeliveryTask();
  const deleteSprint = useDeleteSprint();
  const navigate = useNavigate({ from: Route.fullPath });
  const { id: openId } = Route.useSearch();

  // Which sprint a new task should land in. `{ sprintId: null }` is the backlog;
  // `null` means the dialog is closed — so a deliberate "no sprint" stays
  // distinguishable from "not asking".
  const [addingTaskIn, setAddingTaskIn] = useState<{ sprintId: string | null } | null>(null);
  // `{ sprint: null }` opens the dialog in create mode, `{ sprint }` in edit mode.
  const [sprintDialog, setSprintDialog] = useState<{ sprint: SprintRow | null } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Collapsed state is an override on top of the timing default (past sprints
  // start collapsed), so groups behave sensibly before the data has loaded.
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({});

  const tasks = tasksQ.data ?? [];
  const sprints = sprintsQ.data ?? [];
  const workstreams = workstreamsQ.data ?? [];
  const profiles = profilesQ.data ?? [];

  const today = new Date();
  const blockByKey = new Map(workstreams.map((w) => [w.key, w]));
  const emailById = new Map(profiles.map((p) => [p.id, p.email ?? "(no email)"]));

  const counts: Record<TaskStatus, number> = { todo: 0, in_progress: 0, done: 0, blocked: 0 };
  for (const t of tasks) counts[t.status]++;

  const tasksBySprint = new Map<string, DeliveryTaskRow[]>();
  for (const t of tasks) {
    const key = t.sprint_id ?? BACKLOG_GROUP_ID;
    const bucket = tasksBySprint.get(key);
    if (bucket) bucket.push(t);
    else tasksBySprint.set(key, [t]);
  }

  const currentSprint = sprints.find((s) => sprintTiming(s, today) === "current") ?? null;
  const backlogTasks = tasksBySprint.get(BACKLOG_GROUP_ID) ?? [];

  const patchTask = (id: string, patch: DeliveryTaskPatch) =>
    updateTask.mutate({ id, patch }, failsWith("Couldn’t save the change"));
  const moveTaskToSprint = (taskId: string, sprintId: string | null) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.sprint_id === sprintId) return;
    updateTask.mutate(
      { id: taskId, patch: { sprint_id: sprintId } },
      failsWith("Couldn’t move the task")
    );
  };

  // One place that owns "is this group open": the stored default (finished
  // sprints start collapsed, backlog open) plus any explicit user override.
  const groupDefaults = new Map<string, boolean>(
    sprints.map((s) => [s.id, sprintTiming(s, today) !== "past"])
  );
  groupDefaults.set(BACKLOG_GROUP_ID, true);
  const groupIds = [...groupDefaults.keys()];
  const isGroupOpen = (id: string) => openOverrides[id] ?? groupDefaults.get(id) ?? true;
  // Read the previous value off `prev`, not off the render-scope state: two
  // toggles batched into one commit would otherwise collapse to a single flip.
  const toggleGroup = (id: string) =>
    setOpenOverrides((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? groupDefaults.get(id) ?? true),
    }));
  const allOpen = groupIds.every(isGroupOpen);
  const setAllGroups = (open: boolean) =>
    setOpenOverrides(Object.fromEntries(groupIds.map((id) => [id, open])));

  const openTask = openId ? (tasks.find((t) => t.id === openId) ?? null) : null;
  const closeDrawer = () => void navigate({ search: {} });

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Delivery Work"
        icon={ListTodo}
        numeral={String(counts.in_progress).padStart(2, "0")}
        numeralTone="amber"
        numeralSuffix={`/ ${String(tasks.length).padStart(2, "0")}`}
        title="Sprints"
        summary="Delivery work in weekly sprints. Drag a task onto another sprint — or use its Sprint dropdown — to move it. Blocks are an optional tag."
        meta={[
          { label: "In Progress", value: counts.in_progress, tone: "amber" },
          { label: "To Do", value: counts.todo },
          { label: "Blocked", value: counts.blocked, tone: "red" },
          { label: "Done", value: counts.done, tone: "green" },
        ]}
        actions={
          <div className="flex gap-2 print:hidden">
            <ActionButton
              label={allOpen ? "Collapse all" : "Expand all"}
              icon={allOpen ? ChevronsDownUp : ChevronsUpDown}
              surface="hero"
              onClick={() => setAllGroups(!allOpen)}
            />
            <ActionButton
              label="New sprint"
              icon={CalendarPlus}
              surface="hero"
              onClick={() => setSprintDialog({ sprint: null })}
            />
            <ActionButton
              label="New task"
              icon={Plus}
              surface="hero"
              onClick={() => setAddingTaskIn({ sprintId: currentSprint?.id ?? null })}
            />
          </div>
        }
      />

      {sprintsQ.isLoading || tasksQ.isLoading ? (
        <p className="text-muted-foreground py-8 text-center text-xs italic">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {sprints.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-xs italic">
              No sprints yet — “New sprint” creates the current week.
            </p>
          ) : null}

          {sprints.map((sprint) => {
            const timing = sprintTiming(sprint, today);
            return (
              <SprintSection
                key={sprint.id}
                title={sprint.name}
                subtitle={formatSprintRange(sprint.start_date, sprint.end_date)}
                tone={timing}
                tasks={tasksBySprint.get(sprint.id) ?? []}
                sprints={sprints}
                assignees={profiles}
                blockCodeFor={(t) =>
                  t.workstream_key ? (blockByKey.get(t.workstream_key)?.code ?? null) : null
                }
                assigneeNameFor={(t) =>
                  t.assignee_id ? (emailById.get(t.assignee_id) ?? null) : null
                }
                open={isGroupOpen(sprint.id)}
                onToggle={() => toggleGroup(sprint.id)}
                onPatchTask={patchTask}
                onOpenTask={(id) => void navigate({ search: { id } })}
                onDropTask={(taskId) => moveTaskToSprint(taskId, sprint.id)}
                onAddTask={() => setAddingTaskIn({ sprintId: sprint.id })}
                onEdit={() => setSprintDialog({ sprint })}
                onDelete={() =>
                  deleteSprint.mutate(sprint.id, failsWith("Couldn’t delete the sprint"))
                }
                draggingId={draggingId}
                onDragStartTask={setDraggingId}
                onDragEndTask={() => setDraggingId(null)}
              />
            );
          })}

          <SprintSection
            title="Backlog"
            subtitle="no sprint"
            tone="backlog"
            tasks={backlogTasks}
            sprints={sprints}
            assignees={profiles}
            blockCodeFor={(t) =>
              t.workstream_key ? (blockByKey.get(t.workstream_key)?.code ?? null) : null
            }
            assigneeNameFor={(t) => (t.assignee_id ? (emailById.get(t.assignee_id) ?? null) : null)}
            open={isGroupOpen(BACKLOG_GROUP_ID)}
            onToggle={() => toggleGroup(BACKLOG_GROUP_ID)}
            onPatchTask={patchTask}
            onOpenTask={(id) => void navigate({ search: { id } })}
            onDropTask={(taskId) => moveTaskToSprint(taskId, null)}
            onAddTask={() => setAddingTaskIn({ sprintId: null })}
            onEdit={null}
            onDelete={null}
            draggingId={draggingId}
            onDragStartTask={setDraggingId}
            onDragEndTask={() => setDraggingId(null)}
          />
        </div>
      )}

      <Sheet
        open={!!openId}
        onOpenChange={(next) => {
          if (!next) closeDrawer();
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Task detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the full task.</SheetDescription>
          {openTask ? (
            <DeliveryTaskDrawerContent
              task={openTask}
              sprints={sprints}
              workstreams={workstreams}
              assignees={profiles}
              onPatch={(patch) => patchTask(openTask.id, patch)}
              onDelete={() => {
                deleteTask.mutate(openTask.id, failsWith("Couldn’t delete the task"));
                closeDrawer();
              }}
            />
          ) : (
            <p className="text-muted-foreground p-6 text-xs italic">
              {tasksQ.isLoading ? "Loading task…" : "That task no longer exists."}
            </p>
          )}
        </SheetContent>
      </Sheet>

      {sprintDialog ? (
        <SprintDialog
          sprint={sprintDialog.sprint}
          onClose={() => setSprintDialog(null)}
          defaultName={nextSprintName(sprints)}
          defaultStart={nextSprintStart(sprints, today)}
        />
      ) : null}

      {addingTaskIn ? (
        <CreateDeliveryTaskDialog
          onClose={() => setAddingTaskIn(null)}
          sprints={sprints}
          workstreams={workstreams}
          assignees={profiles}
          defaultSprintId={addingTaskIn.sprintId}
        />
      ) : null}
    </div>
  );
}
